import { FC, useCallback } from "react";
import { useNavigate, useParams } from "react-router";

import { Icon, Link, LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Title } from "capitalrx-components-2.0/lib/typography";

import { PricingLevelsTypes } from "@/types";

import { useGetPricingLevel, useUpdatePricingLevel } from "../../api";
import { PricingLevelFormPage } from "../../components";
import { API_URL } from "../../constants";
import { useFailureHandler, useSuccessHandler } from "../../hooks";
import { PageContent } from "../../layouts";
import { formatIncommingData, formatOutgoingData, formatSubmittingData } from "../../utils";

interface UrlParams {
  id?: string;
}

const PricingLevelUpdate: FC = () => {
  const { id }: UrlParams = useParams();
  const navigate = useNavigate();
  const failureHandler = useFailureHandler();
  const successHandler = useSuccessHandler({
    invalidates: [API_URL.PRICING_LEVEL_GET],
    message: "The pricing level details were updated successfully",
  });

  const { pricingLevel, isFetching } = useGetPricingLevel(id);

  const title = `Pricing Level #${pricingLevel?.id}`;

  const { mutateAsync, isPending: isLoading } = useUpdatePricingLevel({ onSuccess: successHandler, onError: failureHandler });

  const handlePricingLevelDetailsSubmit = useCallback(
    async (data: PricingLevelsTypes.PricingLevelBase): Promise<void> => {
      const formattedData = formatOutgoingData(data);
      await mutateAsync(formatSubmittingData(formattedData));
    },
    [mutateAsync],
  );

  const handlePricingLevelDetailsCancel = useCallback((): void => {
    navigate("/pricing-levels");
  }, [navigate]);

  if (!pricingLevel?.id) {
    return null;
  }

  return (
    <LoadingGroup loading={isFetching || isLoading}>
      <Stack spacing="mini" alignment="left">
        <Link to={"/pricing-levels"}>
          <Shelf spacing="mini">
            <Icon name="arrow-left" color={"turquoise"} />
            Pricing Levels
          </Shelf>
        </Link>
        <Title>{title}</Title>
      </Stack>
      <PageContent canView={true} pageTitle={""}>
        <PricingLevelFormPage
          isEditMode={true}
          handleSubmit={handlePricingLevelDetailsSubmit}
          handleCancel={handlePricingLevelDetailsCancel}
          defaultValues={{ ...formatIncommingData(pricingLevel) }}
        />
      </PageContent>
    </LoadingGroup>
  );
};

export default PricingLevelUpdate;
